import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { trackFormView } from '../../tracking';
import { LEAD_FORM_ID } from '../../utils/scroll';
import { useLeadForm } from './useLeadForm';
import { TextField, SelectField } from './Field';
import { ConsentCheckbox } from './ConsentCheckbox';
import { ErrorState } from './ErrorState';
import { SuccessPanel } from './SuccessPanel';
const VEHICLE_OPTIONS = ['suv', 'sedan', 'family', 'hybrid-ev', 'not-sure'];
const INTENT_OPTIONS = ['buy', 'lease', 'trade-in-buy', 'not-sure'];
const CREDIT_OPTIONS = ['excellent', 'good', 'fair', 'rebuilding', 'first-time', 'not-sure'];
export function LeadForm() {
  const {
    t
  } = useLanguage();
  const {
    values,
    errors,
    expanded,
    status,
    setField,
    handleSubmit,
    retry
  } = useLeadForm();
  const sectionRef = useRef<HTMLElement>(null);
  const viewed = useRef(false);
  const reduce = useReducedMotion();
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && !viewed.current) {
          viewed.current = true;
          trackFormView();
        }
      });
    }, {
      threshold: 0.25
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  const options = (keys: string[]) => keys.map(value => ({
    value,
    label: t(`form.opt.${value}`)
  }));
  return <section id={LEAD_FORM_ID} ref={sectionRef} className="relative w-full overflow-hidden bg-ink" data-id="element-142" data-mp="2o3d0tateu3ya">
      <div aria-hidden="true" className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-gold/10 blur-[110px]" data-id="element-143" data-mp="2j48tkpq1qgal" />
      <div aria-hidden="true" className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-kred/10 blur-[110px]" data-id="element-144" data-mp="29743wfwzs2p1" />

      <div className="relative mx-auto w-full max-w-2xl px-4 py-16 sm:px-6 md:py-24" data-id="element-145" data-mp="1ulghsp78ao3j">
        <h2 className="font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-[-0.02em] text-white sm:text-4xl" data-id="element-146" data-mp="1unt6dl7hrwpk">
          {t('form.title')}
        </h2>
        <p className="mt-3 text-base text-white/60" data-id="element-147" data-mp="1lmcxjhwbs2ng">{t('form.sub')}</p>

        <div className="mt-8" data-id="element-148" data-mp="1qj3f69ns36az">
          {status === 'success' ? <SuccessPanel firstName={values.firstName} data-id="element-149" data-mp="2e9pk3s2hkagr" /> : <form onSubmit={handleSubmit} noValidate className="space-y-5" data-id="element-150" data-mp="2ebk516drc2ku">
              {/* Honeypot — hidden from users, bots that fill it are rejected. */}
              <div className="hidden" aria-hidden="true" data-id="element-151" data-mp="24il7v04733pm">
                <label htmlFor="company" data-id="element-152" data-mp="29ir12kq44y9t">Company</label>
                <input id="company" name="company" type="text" tabIndex={-1} autoComplete="off" value={values.company} onChange={e => setField('company', e.target.value)} data-id="element-153" data-mp="29h65qsvz5baw" />
              </div>

              <TextField id="firstName" label={t('form.firstName')} value={values.firstName} onChange={v => setField('firstName', v)} error={errors.firstName} autoComplete="given-name" data-id="element-154" data-mp="37c8hyga3io8v" />
              <TextField id="phone" label={t('form.phone')} type="tel" inputMode="tel" value={values.phone} onChange={v => setField('phone', v)} error={errors.phone} autoComplete="tel" data-id="element-155" data-mp="2sezmbadreybc" />

              <AnimatePresence initial={false} data-id="element-156" data-mp="2xdt073wykrc1">
                {expanded && <motion.div key="more" initial={reduce ? undefined : {
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={reduce ? undefined : {
              opacity: 0
            }} transition={{
              duration: 0.4,
              ease: [0.22, 1, 0.36, 1]
            }} className="space-y-5" data-id="element-157" data-mp="hmoe6596gmxr">
                    <TextField id="email" label={`${t('form.email')} (${t('form.optional')})`} type="email" inputMode="email" value={values.email} onChange={v => setField('email', v)} error={errors.email} autoComplete="email" data-id="element-158" data-mp="2sabfjqq4kmak" />
                    <SelectField id="vehicleInterest" label={t('form.vehicle')} placeholder={t('form.choose')} value={values.vehicleInterest} onChange={v => setField('vehicleInterest', v as never)} options={options(VEHICLE_OPTIONS)} data-id="element-159" data-mp="3b0jc1pzbkehs" />
                    <SelectField id="purchaseIntent" label={t('form.intent')} placeholder={t('form.choose')} value={values.purchaseIntent} onChange={v => setField('purchaseIntent', v as never)} options={options(INTENT_OPTIONS)} data-id="element-160" data-mp="1ddqmzozjxceu" />
                    <SelectField id="creditSituation" label={t('form.credit')} placeholder={t('form.choose')} value={values.creditSituation} onChange={v => setField('creditSituation', v as never)} options={options(CREDIT_OPTIONS)} data-id="element-161" data-mp="1w3p2ur9lc9t1" />
                  </motion.div>}
              </AnimatePresence>

              <ConsentCheckbox checked={values.consent} onChange={v => setField('consent', v)} error={errors.consent} data-id="element-162" data-mp="2ft0ongzucwc5" />

              {status === 'error' && <ErrorState onRetry={retry} data-id="element-163" data-mp="2tzay5yp3mh6m" />}

              <button type="submit" disabled={status === 'sending'} className="min-h-[56px] w-full bg-gold px-6 text-sm font-bold uppercase tracking-[0.14em] text-golddeep transition-transform hover:-translate-y-[1px] disabled:cursor-wait disabled:opacity-70" data-id="element-164" data-mp="2ywkln8sxgirt">
                {status === 'sending' ? t('form.sending') : t('form.submit')}
              </button>
              <p className="text-center text-xs text-white/45" data-id="element-165" data-mp="2g6mdytt1krop">{t('form.note')}</p>
            </form>}
        </div>
      </div>
    </section>;
}